import {Injectable, inject} from '@angular/core';
import {AuthService} from './auth.service';
import {
  EnterprisePermissions,
  EnterpriseRole,
  FULL_PERMISSIONS,
  getPermissions
} from '../enums/enterprise-role';

/**
 * Single source of truth for "is this user allowed to…".
 *
 * Every enterprise permission check in the app must go through here so the
 * rules live in one place instead of being re-derived (and drifting) in each
 * component. NOTE: this gates the UI only — server-side enforcement lives in
 * the backend routes and Firestore rules.
 */
@Injectable({providedIn: 'root'})
export class PermissionsService {
  private auth = inject(AuthService);

  /** The current user's role, or null when they are not an enterprise member. */
  get role(): EnterpriseRole | null {
    const info = this.auth.currentUserInfo;
    if (!info || !info.enterpriseUid) {
      return null;
    }
    return (info.enterpriseRole as EnterpriseRole) || EnterpriseRole.USER;
  }

  /** True for the account that owns the subscription (or any non-org user). */
  get isOwner(): boolean {
    return this.role === null;
  }

  get permissions(): EnterprisePermissions {
    return this.isOwner ? FULL_PERMISSIONS : getPermissions(this.role);
  }

  can(permission: keyof EnterprisePermissions): boolean {
    return !!this.permissions[permission];
  }

  get canManageUsers(): boolean {
    return this.can('manageUsers');
  }

  get canManageRoles(): boolean {
    return this.can('manageRoles');
  }

  get canViewAllProjects(): boolean {
    return this.can('viewAllProjects');
  }

  get canTransferProjects(): boolean {
    return this.can('transferProjects');
  }

  get canEditProjects(): boolean {
    return this.can('editProjects');
  }

  get canDeleteProjects(): boolean {
    return this.can('deleteProjects');
  }

  get canManageProducts(): boolean {
    return this.can('manageProducts');
  }

  /** Read-only members get a clearly explained, non-editable experience. */
  get isReadOnly(): boolean {
    return !this.canEditProjects;
  }
}
